import React from 'react';

export type Scene = 'day' | 'night' | 'rain';

// Small line icons for the card titles (24x24, stroke = currentColor).
const LineIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

export const WindIcon = () => (
    <LineIcon>
        <path d="M12.8 19.6A2 2 0 1 0 14 16H2" />
        <path d="M17.5 8a2.5 2.5 0 1 1 2 4H2" />
        <path d="M9.8 4.4A2 2 0 1 1 11 8H2" />
    </LineIcon>
);

export const DropIcon = () => (
    <LineIcon>
        <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
    </LineIcon>
);

export const GaugeIcon = () => (
    <LineIcon>
        <path d="m12 14 4-4" />
        <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </LineIcon>
);

export const RainIcon = () => (
    <LineIcon>
        <path d="M4 14.9A7 7 0 1 1 15.7 8h1.8a4.5 4.5 0 0 1 2.5 8.2" />
        <path d="M16 14v6" />
        <path d="M8 14v6" />
        <path d="M12 16v6" />
    </LineIcon>
);

export const SunIcon = () => (
    <LineIcon>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </LineIcon>
);

export const ShieldIcon = () => (
    <LineIcon>
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </LineIcon>
);

export const HomeIcon = () => (
    <LineIcon>
        <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
        <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </LineIcon>
);

const CLOUD_PATH = 'M30 70h42a16 16 0 0 0 0-32a22 22 0 0 0-42-4a18 18 0 0 0 0 36z';

// Large animated illustration shown next to the temperature.
export const SceneIcon: React.FC<{ scene: Scene }> = ({ scene }) => {
    if (scene === 'rain') {
        return (
            <svg className="scene-icon rain-icon" viewBox="0 0 100 100" aria-hidden="true">
                <defs>
                    <linearGradient id="cloud-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#ffffff" />
                        <stop offset="1" stopColor="#c9d6e8" />
                    </linearGradient>
                </defs>
                <g className="drops" stroke="#8fd0ff" strokeWidth={4} strokeLinecap="round">
                    <line x1="36" y1="66" x2="32" y2="78" />
                    <line x1="52" y1="66" x2="48" y2="78" />
                    <line x1="68" y1="66" x2="64" y2="78" />
                </g>
                <path d={CLOUD_PATH} transform="translate(0 -6)" fill="url(#cloud-fill)" />
            </svg>
        );
    }

    if (scene === 'night') {
        return (
            <svg className="scene-icon moon-icon" viewBox="0 0 100 100" aria-hidden="true">
                <defs>
                    <radialGradient id="moon-glow">
                        <stop offset="0.45" stopColor="#c7d2fe" stopOpacity="0.45" />
                        <stop offset="1" stopColor="#c7d2fe" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="moon-fill" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#fffbea" />
                        <stop offset="1" stopColor="#e2e8ff" />
                    </linearGradient>
                    <mask id="moon-cut">
                        <rect width="100" height="100" fill="#fff" />
                        <circle cx="64" cy="38" r="22" fill="#000" />
                    </mask>
                </defs>
                <circle className="glow" cx="50" cy="50" r="46" fill="url(#moon-glow)" />
                <circle cx="50" cy="50" r="26" fill="url(#moon-fill)" mask="url(#moon-cut)" />
                <g fill="#fff">
                    <circle className="twinkle" cx="78" cy="22" r="2" />
                    <circle className="twinkle t2" cx="86" cy="48" r="1.4" />
                    <circle className="twinkle t3" cx="68" cy="76" r="1.6" />
                </g>
            </svg>
        );
    }

    return (
        <svg className="scene-icon sun-icon" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
                <radialGradient id="sun-glow">
                    <stop offset="0.3" stopColor="#ffd56b" stopOpacity="0.7" />
                    <stop offset="1" stopColor="#ffd56b" stopOpacity="0" />
                </radialGradient>
                <linearGradient id="sun-fill" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#fff3b0" />
                    <stop offset="0.5" stopColor="#ffcf3f" />
                    <stop offset="1" stopColor="#ff9f1c" />
                </linearGradient>
            </defs>
            <circle className="glow" cx="50" cy="50" r="48" fill="url(#sun-glow)" />
            <g className="rays" stroke="#ffd24a" strokeWidth={4} strokeLinecap="round">
                <line x1="50" y1="8" x2="50" y2="18" />
                <line x1="50" y1="82" x2="50" y2="92" />
                <line x1="8" y1="50" x2="18" y2="50" />
                <line x1="82" y1="50" x2="92" y2="50" />
                <line x1="20.3" y1="20.3" x2="27.4" y2="27.4" />
                <line x1="72.6" y1="72.6" x2="79.7" y2="79.7" />
                <line x1="20.3" y1="79.7" x2="27.4" y2="72.6" />
                <line x1="72.6" y1="27.4" x2="79.7" y2="20.3" />
            </g>
            <circle cx="50" cy="50" r="22" fill="url(#sun-fill)" />
        </svg>
    );
};
